import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { AuthContext } from '../App';
import { apiClient } from '../services/api';
import { getCustomerId, hydrateUser } from '../utils/authUser';

const BINDING_TOKEN_STORAGE_KEY = 'marketplace_binding_token';

function Callback() {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [error, setError] = useState('');

  const clearStoredBindingToken = () => {
    try {
      window.localStorage.removeItem(BINDING_TOKEN_STORAGE_KEY);
    } catch (storageError) {
      console.warn('Failed to clear binding token from storage:', storageError);
    }
  };

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Handle the OAuth callback
      let user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      user = await hydrateUser(user);

      let pendingBindingToken = '';
      try {
        pendingBindingToken = window.localStorage.getItem(BINDING_TOKEN_STORAGE_KEY) || '';
      } catch (storageError) {
        console.warn('Failed to read binding token from storage:', storageError);
      }

      if (pendingBindingToken) {
        try {
          await apiClient.post('/customers', { binding_token: pendingBindingToken });
          clearStoredBindingToken();
          user = await Auth.currentAuthenticatedUser({ bypassCache: true });
          user = await hydrateUser(user);
        } catch (bindingError) {
          const message = (bindingError.response?.data?.error || bindingError.message || '').toLowerCase();
          if (message.includes('already bound to a different customer')) {
            clearStoredBindingToken();
            setError(
              'Wrong user for this Marketplace setup link. Sign in with the portal user ' +
              'already linked to this subscription, or sign out and use a different user.'
            );
            return;
          }
          if (message.includes('binding token')) {
            clearStoredBindingToken();
            if (getCustomerId(user)) {
              // The user is already linked; ignore stale Marketplace setup tokens.
            } else {
              setError(
                'Marketplace setup link is invalid or expired. Return to AWS Marketplace ' +
                'and click "Set up your account" again.'
              );
              return;
            }
          } else {
            throw bindingError;
          }
        }
      }

      if (!getCustomerId(user)) {
        const emailAddress = user?.attributes?.email || user?.username || '';
        await apiClient.post('/customers', { email: emailAddress });
        user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        user = await hydrateUser(user);
      }

      setUser(user);
      navigate('/');
    } catch (callbackError) {
      console.error('Callback error:', callbackError);
      setError('Sign-in callback failed. Please sign in again.');
    }
  };

  if (error) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>Marketplace Setup Error</h2>
          <p style={{ marginTop: '12px', marginBottom: '20px' }}>{error}</p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%', marginBottom: '12px' }}
            onClick={() => navigate('/login')}
          >
            Back to Sign In
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ width: '100%' }}
            onClick={async () => {
              try {
                await Auth.signOut();
              } catch (signOutError) {
                console.warn('Sign out failed:', signOutError);
              }
              navigate('/login');
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="loading-screen">
      <div className="loading-spinner"></div>
    </div>
  );
}

export default Callback;
